import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';
import * as executionApi from '../../services/execution-api.js';

@customElement('cp-preflight-questions-panel')
export class CpPreflightQuestionsPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) ticketId = '';
  @state() private _questions: string[] = [];
  @state() private _answers: string[] = [];
  @state() private _submitting = false;

  private _wsHandler: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload: Record<string, unknown> }>).detail;
      if (msg.type !== 'execution:preflight-questions') return;
      if ((msg.payload?.ticketId as string) !== this.ticketId) return;
      const questions = (msg.payload?.questions as string[]) ?? [];
      this._questions = questions;
      this._answers = new Array(questions.length).fill('');
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  private _onInput(i: number, value: string): void {
    this._answers = this._answers.map((a, idx) => (idx === i ? value : a));
  }

  private async _onSubmit(): Promise<void> {
    if (this._submitting) return;
    this._submitting = true;
    try {
      const pairs = this._questions.map((q, i) => ({ question: q, answer: this._answers[i] ?? '' }));
      await executionApi.preflightAnswer(this.ticketId, pairs);
      this._questions = [];
      this._answers = [];
    } catch {
      // Keep panel open so user can retry
    } finally {
      this._submitting = false;
    }
  }

  render() {
    if (!this._questions.length) return nothing;

    const allAnswered = this._answers.every(a => a.trim().length > 0);

    return html`
      <div class="cp-preflight-panel">
        <div class="cp-preflight-panel__header">
          <span class="cp-preflight-panel__icon">?</span>
          <span class="cp-preflight-panel__title">Vor Ausfuehrung klaeren</span>
        </div>
        ${this._questions.map((q, i) => html`
          <div class="cp-preflight-panel__question">
            <label class="cp-preflight-panel__label">${i + 1}. ${q}</label>
            <textarea
              class="cp-preflight-panel__textarea"
              .value=${this._answers[i] ?? ''}
              @input=${(e: Event) => this._onInput(i, (e.target as HTMLTextAreaElement).value)}
              placeholder="Antwort..."
              rows="2"
            ></textarea>
          </div>
        `)}
        <button
          class="cp-preflight-panel__submit"
          ?disabled=${this._submitting || !allAnswered}
          @click=${this._onSubmit}
        >${this._submitting ? 'Wird gestartet...' : 'Ausfuehrung starten'}</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-preflight-questions-panel': CpPreflightQuestionsPanel;
  }
}
